import { Environment } from '@rhg/constants';
import { logger } from '@rhg/libs/logger';
import { createContext, ReactNode, useContext, useEffect } from 'react';

interface AppConfigProviderProps {
    children: ReactNode | ReactNode[];
    value: ServerRuntimeConfig;
    isMocked?: boolean;
}

const serverRuntimeConfigDefaultValue: ServerRuntimeConfig = {
    NEXT_PUBLIC_ENV: Environment.LOCAL,
    NEXT_PUBLIC_VERSION: '',
    NEXT_PUBLIC_SITE_BASEURL: '',

    NEXT_PUBLIC_ANALYTICS_DEBUG: true,
    NEXT_PUBLIC_ANALYTICS_OPT_IN_COUNTRIES: [],

    NEXT_PUBLIC_AMPLITUDE_TOKEN: '',
    NEXT_PUBLIC_AMPLITUDE_BASEURL: '',

    NEXT_PUBLIC_DATADOG_APP_ID: '',
    NEXT_PUBLIC_DATADOG_CLIENT_TOKEN: '',
    NEXT_PUBLIC_DATADOG_SITE: '',
    NEXT_PUBLIC_DATADOG_SERVICE: '',
    NEXT_PUBLIC_DATADOG_BASEURL: '',

    NEXT_PUBLIC_AUTH_BASEURL: '',
    NEXT_PUBLIC_AUTH_DOMAIN: '',
    NEXT_PUBLIC_AUTH_CLIENT_ID: '',
    NEXT_PUBLIC_AUTH_AUDIENCE: '',
    NEXT_PUBLIC_AUTH_EMAIL_CONNECTION_ENABLED: false,

    NEXT_PUBLIC_API_BASEURL: '',
    NEXT_PUBLIC_CALM_API_BASEURL: '',
    NEXT_PUBLIC_CALM_PUBLIC_ASSETS_BASEURLS: [],
    NEXT_PUBLIC_CALM_ACCOUNTS_URL: '',

    NEXT_PUBLIC_LINK_APPLE_APP_STORE: '',
    NEXT_PUBLIC_LINK_CALM_APPLE_APP_STORE: '',
    NEXT_PUBLIC_LINK_DEEPLINK_BASE_URL: '',

    NEXT_PUBLIC_LINK_GOOGLE_PLAY: '',
    NEXT_PUBLIC_LINK_CALM_GOOGLE_PLAY: '',

    NEXT_PUBLIC_LANDING_PROGRAM: '',
    NEXT_PUBLIC_LANDING_NARRATORS: [],
    NEXT_PUBLIC_LANDING_CLINICAL_AUTHORS: [],

    NEXT_PUBLIC_ONBOARDING_PACK_CLASS: '',
    NEXT_PUBLIC_SLEEP_STORIES_PACK_CLASS: '',
    NEXT_PUBLIC_DAILIES_PACK_CLASS: '',
    NEXT_PUBLIC_DISCOVER_PAGE_BASE_PACK: '',
    NEXT_PUBLIC_DISCOVER_DAILIES_QUICK_NAV_PACK: '',
    NEXT_PUBLIC_ONBOARDING_INTRO_PROGRAM_GOALS_TOPICS: '',
    NEXT_PUBLIC_ONBOARDING_INTRO_PROGRAM_CONDITIONS: '',
    NEXT_PUBLIC_ONBOARDING_INTRO_PROGRAM_SCREENING: '',
    NEXT_PUBLIC_ONBOARDING_INTRO_PROGRAM_CARE_PLAN: '',
    NEXT_PUBLIC_ONBOARDING_BACKGROUND_PROGRAM_OTHER: ['', ''],
    NEXT_PUBLIC_ONBOARDING_BACKGROUND_GUIDE_GOALS: ['', ''],
    NEXT_PUBLIC_ONBOARDING_BACKGROUND_GUIDE_TOPICS: ['', ''],
    NEXT_PUBLIC_ONBOARDING_BACKGROUND_GUIDE_CONDITIONS: ['', ''],
    NEXT_PUBLIC_ONBOARDING_BACKGROUND_GUIDE_SCREENING_INTRO: ['', ''],
    NEXT_PUBLIC_ONBOARDING_BACKGROUND_GUIDE_SCREENING_OUTRO: ['', ''],
    NEXT_PUBLIC_ONBOARDING_BACKGROUND_PROGRAM_SCREENING_QUESTIONS: ['', ''],
    NEXT_PUBLIC_ONBOARDING_BACKGROUND_GUIDE_SCREENING_QUESTIONS: [],
    NEXT_PUBLIC_ONBOARDING_PROGRAM_COMPLETION_CELEBRATION_ID: '',

    NEXT_PUBLIC_CONDITIONS_HIERARCHY_ENABLED: true,
    NEXT_PUBLIC_AGE_GENDER_ENABLED_ISO_DATETIME: new Date('2000-01-01'),
    NEXT_PUBLIC_THEME_SELECTOR_ENABLED: true,

    NEXT_PUBLIC_DEVTOOLS_ENABLED: true,

    NEXT_PUBLIC_POST_SCREENING_PROMO_CODE: '',
    NEXT_PUBLIC_CALM_PROMO_LINK: '',
    NEXT_PUBLIC_STANDALONE_SCREENING_INTRO_PACK_CLASS: '',
    NEXT_PUBLIC_STANDALONE_SCREENING_INTRO_PROGRAM_ID: '',

    NEXT_PUBLIC_DEFAULT_SSG_CACHE_DUR: '60',
    NEXT_PUBLIC_FTUE_WORKFLOW_ENABLED: false,
    NEXT_PUBLIC_ALLOWED_UNSPONSORED_COUNTRIES: ['', ''],
    NEXT_PUBLIC_SUPPORTED_LOCALES: [],
    NEXT_PUBLIC_RESOURCES_ENABLED: false,
    NEXT_PUBLIC_SEARCH_ENABLED: true,
};

export const extractAppConfig = (): ServerRuntimeConfig => {
    /**
     * @param {string} value Dynamic process.env lookups are NOT inlined, so we need to explicitly include them
     * SEE: https://nextjs.org/docs/pages/building-your-application/configuring/environment-variables#bundling-environment-variables-for-the-browser
     */
    const parseEnv = <
        T extends
            | string
            | boolean
            | Date
            | string[]
            | [string, string]
            | [string, string][]
    >(
        property: keyof ServerRuntimeConfig,
        type: 'string' | 'boolean' | 'json' | 'date',
        value?: string
    ): T => {
        const defaultValue = serverRuntimeConfigDefaultValue[property];

        if (value === undefined) {
            return defaultValue as T;
        }

        if (type === 'boolean') {
            return (value === 'true') as T;
        } else if (type === 'json') {
            return JSON.parse(value) as T;
        } else if (type === 'date') {
            return new Date(value) as T;
        } else {
            return value as T;
        }
    };

    return {
        NEXT_PUBLIC_ENV: parseEnv<string>(
            'NEXT_PUBLIC_ENV',
            'string',
            process.env.NEXT_PUBLIC_ENV
        ),
        NEXT_PUBLIC_VERSION: parseEnv<string>(
            'NEXT_PUBLIC_VERSION',
            'string',
            process.env.NEXT_PUBLIC_VERSION
        ),
        NEXT_PUBLIC_SITE_BASEURL: parseEnv<string>(
            'NEXT_PUBLIC_SITE_BASEURL',
            'string',
            process.env.NEXT_PUBLIC_SITE_BASEURL
        ),
        NEXT_PUBLIC_ANALYTICS_DEBUG: parseEnv<boolean>(
            'NEXT_PUBLIC_ANALYTICS_DEBUG',
            'boolean',
            process.env.NEXT_PUBLIC_ANALYTICS_DEBUG
        ),
        NEXT_PUBLIC_ANALYTICS_OPT_IN_COUNTRIES: parseEnv<string[]>(
            'NEXT_PUBLIC_ANALYTICS_OPT_IN_COUNTRIES',
            'json',
            process.env.NEXT_PUBLIC_ANALYTICS_OPT_IN_COUNTRIES
        ),
        NEXT_PUBLIC_AMPLITUDE_TOKEN: parseEnv<string>(
            'NEXT_PUBLIC_AMPLITUDE_TOKEN',
            'string',
            process.env.NEXT_PUBLIC_AMPLITUDE_TOKEN
        ),
        NEXT_PUBLIC_AMPLITUDE_BASEURL: parseEnv<string>(
            'NEXT_PUBLIC_AMPLITUDE_BASEURL',
            'string',
            process.env.NEXT_PUBLIC_AMPLITUDE_BASEURL
        ),
        NEXT_PUBLIC_DATADOG_APP_ID: parseEnv<string>(
            'NEXT_PUBLIC_DATADOG_APP_ID',
            'string',
            process.env.NEXT_PUBLIC_DATADOG_APP_ID
        ),
        NEXT_PUBLIC_DATADOG_CLIENT_TOKEN: parseEnv<string>(
            'NEXT_PUBLIC_DATADOG_CLIENT_TOKEN',
            'string',
            process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN
        ),
        NEXT_PUBLIC_DATADOG_SITE: parseEnv<string>(
            'NEXT_PUBLIC_DATADOG_SITE',
            'string',
            process.env.NEXT_PUBLIC_DATADOG_SITE
        ),
        NEXT_PUBLIC_DATADOG_SERVICE: parseEnv<string>(
            'NEXT_PUBLIC_DATADOG_SERVICE',
            'string',
            process.env.NEXT_PUBLIC_DATADOG_SERVICE
        ),
        NEXT_PUBLIC_DATADOG_BASEURL: parseEnv<string>(
            'NEXT_PUBLIC_DATADOG_BASEURL',
            'string',
            process.env.NEXT_PUBLIC_DATADOG_BASEURL
        ),
        NEXT_PUBLIC_AUTH_AUDIENCE: parseEnv<string>(
            'NEXT_PUBLIC_AUTH_AUDIENCE',
            'string',
            process.env.NEXT_PUBLIC_AUTH_AUDIENCE
        ),
        NEXT_PUBLIC_AUTH_BASEURL: parseEnv<string>(
            'NEXT_PUBLIC_AUTH_BASEURL',
            'string',
            process.env.NEXT_PUBLIC_AUTH_BASEURL
        ),
        NEXT_PUBLIC_AUTH_CLIENT_ID: parseEnv<string>(
            'NEXT_PUBLIC_AUTH_CLIENT_ID',
            'string',
            process.env.NEXT_PUBLIC_AUTH_CLIENT_ID
        ),
        NEXT_PUBLIC_AUTH_DOMAIN: parseEnv<string>(
            'NEXT_PUBLIC_AUTH_DOMAIN',
            'string',
            process.env.NEXT_PUBLIC_AUTH_DOMAIN
        ),
        NEXT_PUBLIC_AUTH_EMAIL_CONNECTION_ENABLED: parseEnv<boolean>(
            'NEXT_PUBLIC_AUTH_EMAIL_CONNECTION_ENABLED',
            'boolean',
            process.env.NEXT_PUBLIC_AUTH_EMAIL_CONNECTION_ENABLED
        ),
        NEXT_PUBLIC_API_BASEURL: parseEnv<string>(
            'NEXT_PUBLIC_API_BASEURL',
            'string',
            process.env.NEXT_PUBLIC_API_BASEURL
        ),
        NEXT_PUBLIC_CALM_API_BASEURL: parseEnv<string>(
            'NEXT_PUBLIC_CALM_API_BASEURL',
            'string',
            process.env.NEXT_PUBLIC_CALM_API_BASEURL
        ),
        NEXT_PUBLIC_CALM_ACCOUNTS_URL: parseEnv<string>(
            'NEXT_PUBLIC_CALM_ACCOUNTS_URL',
            'string',
            process.env.NEXT_PUBLIC_CALM_ACCOUNTS_URL
        ),
        NEXT_PUBLIC_CALM_PUBLIC_ASSETS_BASEURLS: parseEnv<string[]>(
            'NEXT_PUBLIC_CALM_PUBLIC_ASSETS_BASEURLS',
            'json',
            process.env.NEXT_PUBLIC_CALM_PUBLIC_ASSETS_BASEURLS
        ),
        NEXT_PUBLIC_LANDING_PROGRAM: parseEnv<string>(
            'NEXT_PUBLIC_LANDING_PROGRAM',
            'string',
            process.env.NEXT_PUBLIC_LANDING_PROGRAM
        ),
        NEXT_PUBLIC_LANDING_NARRATORS: parseEnv<string[]>(
            'NEXT_PUBLIC_LANDING_NARRATORS',
            'json',
            process.env.NEXT_PUBLIC_LANDING_NARRATORS
        ),
        NEXT_PUBLIC_LANDING_CLINICAL_AUTHORS: parseEnv<string[]>(
            'NEXT_PUBLIC_LANDING_CLINICAL_AUTHORS',
            'json',
            process.env.NEXT_PUBLIC_LANDING_CLINICAL_AUTHORS
        ),
        NEXT_PUBLIC_ONBOARDING_PACK_CLASS: parseEnv<string>(
            'NEXT_PUBLIC_ONBOARDING_PACK_CLASS',
            'string',
            process.env.NEXT_PUBLIC_ONBOARDING_PACK_CLASS
        ),
        NEXT_PUBLIC_SLEEP_STORIES_PACK_CLASS: parseEnv<string>(
            'NEXT_PUBLIC_SLEEP_STORIES_PACK_CLASS',
            'string',
            process.env.NEXT_PUBLIC_SLEEP_STORIES_PACK_CLASS
        ),
        NEXT_PUBLIC_DAILIES_PACK_CLASS: parseEnv<string>(
            'NEXT_PUBLIC_DAILIES_PACK_CLASS',
            'string',
            process.env.NEXT_PUBLIC_DAILIES_PACK_CLASS
        ),
        NEXT_PUBLIC_DISCOVER_PAGE_BASE_PACK: parseEnv<string>(
            'NEXT_PUBLIC_DISCOVER_PAGE_BASE_PACK',
            'string',
            process.env.NEXT_PUBLIC_DISCOVER_PAGE_BASE_PACK
        ),
        NEXT_PUBLIC_DISCOVER_DAILIES_QUICK_NAV_PACK: parseEnv<string>(
            'NEXT_PUBLIC_DISCOVER_DAILIES_QUICK_NAV_PACK',
            'string',
            process.env.NEXT_PUBLIC_DISCOVER_DAILIES_QUICK_NAV_PACK
        ),
        NEXT_PUBLIC_ONBOARDING_INTRO_PROGRAM_GOALS_TOPICS: parseEnv<string>(
            'NEXT_PUBLIC_ONBOARDING_INTRO_PROGRAM_GOALS_TOPICS',
            'string',
            process.env.NEXT_PUBLIC_ONBOARDING_INTRO_PROGRAM_GOALS_TOPICS
        ),
        NEXT_PUBLIC_ONBOARDING_INTRO_PROGRAM_CONDITIONS: parseEnv<string>(
            'NEXT_PUBLIC_ONBOARDING_INTRO_PROGRAM_CONDITIONS',
            'string',
            process.env.NEXT_PUBLIC_ONBOARDING_INTRO_PROGRAM_CONDITIONS
        ),
        NEXT_PUBLIC_ONBOARDING_INTRO_PROGRAM_SCREENING: parseEnv<string>(
            'NEXT_PUBLIC_ONBOARDING_INTRO_PROGRAM_SCREENING',
            'string',
            process.env.NEXT_PUBLIC_ONBOARDING_INTRO_PROGRAM_SCREENING
        ),
        NEXT_PUBLIC_ONBOARDING_INTRO_PROGRAM_CARE_PLAN: parseEnv<string>(
            'NEXT_PUBLIC_ONBOARDING_INTRO_PROGRAM_CARE_PLAN',
            'string',
            process.env.NEXT_PUBLIC_ONBOARDING_INTRO_PROGRAM_CARE_PLAN
        ),
        NEXT_PUBLIC_ONBOARDING_PROGRAM_COMPLETION_CELEBRATION_ID:
            parseEnv<string>(
                'NEXT_PUBLIC_ONBOARDING_PROGRAM_COMPLETION_CELEBRATION_ID',
                'string',
                process.env
                    .NEXT_PUBLIC_ONBOARDING_PROGRAM_COMPLETION_CELEBRATION_ID
            ),

        NEXT_PUBLIC_ONBOARDING_BACKGROUND_PROGRAM_OTHER: parseEnv<
            [string, string]
        >(
            'NEXT_PUBLIC_ONBOARDING_BACKGROUND_PROGRAM_OTHER',
            'json',
            process.env.NEXT_PUBLIC_ONBOARDING_BACKGROUND_PROGRAM_OTHER
        ),
        NEXT_PUBLIC_ONBOARDING_BACKGROUND_GUIDE_GOALS: parseEnv<
            [string, string]
        >(
            'NEXT_PUBLIC_ONBOARDING_BACKGROUND_GUIDE_GOALS',
            'json',
            process.env.NEXT_PUBLIC_ONBOARDING_BACKGROUND_GUIDE_GOALS
        ),
        NEXT_PUBLIC_ONBOARDING_BACKGROUND_GUIDE_TOPICS: parseEnv<
            [string, string]
        >(
            'NEXT_PUBLIC_ONBOARDING_BACKGROUND_GUIDE_TOPICS',
            'json',
            process.env.NEXT_PUBLIC_ONBOARDING_BACKGROUND_GUIDE_TOPICS
        ),
        NEXT_PUBLIC_ONBOARDING_BACKGROUND_GUIDE_CONDITIONS: parseEnv<
            [string, string]
        >(
            'NEXT_PUBLIC_ONBOARDING_BACKGROUND_GUIDE_CONDITIONS',
            'json',
            process.env.NEXT_PUBLIC_ONBOARDING_BACKGROUND_GUIDE_CONDITIONS
        ),
        NEXT_PUBLIC_ONBOARDING_BACKGROUND_GUIDE_SCREENING_INTRO: parseEnv<
            [string, string]
        >(
            'NEXT_PUBLIC_ONBOARDING_BACKGROUND_GUIDE_SCREENING_INTRO',
            'json',
            process.env.NEXT_PUBLIC_ONBOARDING_BACKGROUND_GUIDE_SCREENING_INTRO
        ),
        NEXT_PUBLIC_ONBOARDING_BACKGROUND_GUIDE_SCREENING_OUTRO: parseEnv<
            [string, string]
        >(
            'NEXT_PUBLIC_ONBOARDING_BACKGROUND_GUIDE_SCREENING_OUTRO',
            'json',
            process.env.NEXT_PUBLIC_ONBOARDING_BACKGROUND_GUIDE_SCREENING_OUTRO
        ),
        NEXT_PUBLIC_ONBOARDING_BACKGROUND_PROGRAM_SCREENING_QUESTIONS: parseEnv<
            [string, string]
        >(
            'NEXT_PUBLIC_ONBOARDING_BACKGROUND_PROGRAM_SCREENING_QUESTIONS',
            'json',
            process.env
                .NEXT_PUBLIC_ONBOARDING_BACKGROUND_PROGRAM_SCREENING_QUESTIONS
        ),
        NEXT_PUBLIC_ONBOARDING_BACKGROUND_GUIDE_SCREENING_QUESTIONS: parseEnv<
            [string, string][]
        >(
            'NEXT_PUBLIC_ONBOARDING_BACKGROUND_GUIDE_SCREENING_QUESTIONS',
            'json',
            process.env
                .NEXT_PUBLIC_ONBOARDING_BACKGROUND_GUIDE_SCREENING_QUESTIONS
        ),

        NEXT_PUBLIC_LINK_APPLE_APP_STORE: parseEnv<string>(
            'NEXT_PUBLIC_LINK_APPLE_APP_STORE',
            'string',
            process.env.NEXT_PUBLIC_LINK_APPLE_APP_STORE
        ),
        NEXT_PUBLIC_LINK_CALM_APPLE_APP_STORE: parseEnv<string>(
            'NEXT_PUBLIC_LINK_CALM_APPLE_APP_STORE',
            'string',
            process.env.NEXT_PUBLIC_LINK_CALM_APPLE_APP_STORE
        ),
        NEXT_PUBLIC_LINK_GOOGLE_PLAY: parseEnv<string>(
            'NEXT_PUBLIC_LINK_GOOGLE_PLAY',
            'string',
            process.env.NEXT_PUBLIC_LINK_GOOGLE_PLAY
        ),
        NEXT_PUBLIC_LINK_CALM_GOOGLE_PLAY: parseEnv<string>(
            'NEXT_PUBLIC_LINK_CALM_GOOGLE_PLAY',
            'string',
            process.env.NEXT_PUBLIC_LINK_CALM_GOOGLE_PLAY
        ),

        NEXT_PUBLIC_LINK_DEEPLINK_BASE_URL: parseEnv<string>(
            'NEXT_PUBLIC_LINK_DEEPLINK_BASE_URL',
            'string',
            process.env.NEXT_PUBLIC_LINK_DEEPLINK_BASE_URL
        ),

        NEXT_PUBLIC_CONDITIONS_HIERARCHY_ENABLED: parseEnv<boolean>(
            'NEXT_PUBLIC_CONDITIONS_HIERARCHY_ENABLED',
            'boolean',
            process.env.NEXT_PUBLIC_CONDITIONS_HIERARCHY_ENABLED
        ),
        NEXT_PUBLIC_AGE_GENDER_ENABLED_ISO_DATETIME: parseEnv<Date>(
            'NEXT_PUBLIC_AGE_GENDER_ENABLED_ISO_DATETIME',
            'date',
            process.env.NEXT_PUBLIC_AGE_GENDER_ENABLED_ISO_DATETIME
        ),
        NEXT_PUBLIC_THEME_SELECTOR_ENABLED: parseEnv<boolean>(
            'NEXT_PUBLIC_THEME_SELECTOR_ENABLED',
            'boolean',
            process.env.NEXT_PUBLIC_THEME_SELECTOR_ENABLED
        ),
        NEXT_PUBLIC_DEVTOOLS_ENABLED: parseEnv<boolean>(
            'NEXT_PUBLIC_DEVTOOLS_ENABLED',
            'boolean',
            process.env.NEXT_PUBLIC_DEVTOOLS_ENABLED
        ),
        NEXT_PUBLIC_POST_SCREENING_PROMO_CODE: parseEnv<string>(
            'NEXT_PUBLIC_POST_SCREENING_PROMO_CODE',
            'string',
            process.env.NEXT_PUBLIC_POST_SCREENING_PROMO_CODE
        ),

        NEXT_PUBLIC_CALM_PROMO_LINK: parseEnv<string>(
            'NEXT_PUBLIC_CALM_PROMO_LINK',
            'string',
            process.env.NEXT_PUBLIC_CALM_PROMO_LINK
        ),
        NEXT_PUBLIC_STANDALONE_SCREENING_INTRO_PACK_CLASS: parseEnv<string>(
            'NEXT_PUBLIC_STANDALONE_SCREENING_INTRO_PACK_CLASS',
            'string',
            process.env.NEXT_PUBLIC_STANDALONE_SCREENING_INTRO_PACK_CLASS
        ),
        NEXT_PUBLIC_STANDALONE_SCREENING_INTRO_PROGRAM_ID: parseEnv<string>(
            'NEXT_PUBLIC_STANDALONE_SCREENING_INTRO_PROGRAM_ID',
            'string',
            process.env.NEXT_PUBLIC_STANDALONE_SCREENING_INTRO_PROGRAM_ID
        ),

        NEXT_PUBLIC_DEFAULT_SSG_CACHE_DUR: parseEnv<string>(
            'NEXT_PUBLIC_DEFAULT_SSG_CACHE_DUR',
            'string',
            process.env.NEXT_PUBLIC_DEFAULT_SSG_CACHE_DUR
        ),

        NEXT_PUBLIC_FTUE_WORKFLOW_ENABLED: parseEnv<boolean>(
            'NEXT_PUBLIC_FTUE_WORKFLOW_ENABLED',
            'boolean',
            process.env.NEXT_PUBLIC_FTUE_WORKFLOW_ENABLED
        ),
        NEXT_PUBLIC_ALLOWED_UNSPONSORED_COUNTRIES: parseEnv<[string, string]>(
            'NEXT_PUBLIC_ALLOWED_UNSPONSORED_COUNTRIES',
            'json',
            process.env.NEXT_PUBLIC_ALLOWED_UNSPONSORED_COUNTRIES
        ),
        NEXT_PUBLIC_SUPPORTED_LOCALES: parseEnv<string[]>(
            'NEXT_PUBLIC_SUPPORTED_LOCALES',
            'json',
            process.env.NEXT_PUBLIC_SUPPORTED_LOCALES
        ),

        NEXT_PUBLIC_RESOURCES_ENABLED: parseEnv<boolean>(
            'NEXT_PUBLIC_RESOURCES_ENABLED',
            'boolean',
            process.env.NEXT_PUBLIC_RESOURCES_ENABLED
        ),

        NEXT_PUBLIC_SEARCH_ENABLED: parseEnv<boolean>(
            'NEXT_PUBLIC_SEARCH_ENABLED',
            'boolean',
            process.env.NEXT_PUBLIC_SEARCH_ENABLED
        ),
    };
};

const AppConfigContext = createContext<ServerRuntimeConfig>(
    serverRuntimeConfigDefaultValue
);

export const AppConfigProvider = ({
    children,
    value,
    isMocked,
}: AppConfigProviderProps) => {
    useEffect(() => {
        if (
            !isMocked &&
            [Environment.STAGING, Environment.LOCAL].includes(
                value.NEXT_PUBLIC_ENV as Environment
            )
        ) {
            logger.debug('Received AppConfig', { ...value });
        }
    }, [isMocked, value]);

    return (
        <AppConfigContext.Provider value={value}>
            {children}
        </AppConfigContext.Provider>
    );
};

function useAppConfig() {
    return useContext(AppConfigContext);
}

export default useAppConfig;
